// Presentation metadata for AI Agent plans.
//
// Pricing, plan IDs, names, descriptions and the annual discount come from the live
// `GET /agentic-plans` API (see buildAgenticPlans). The API does NOT return credits,
// feature lists, icons or the "Done-for-you" plan, so that presentation-only metadata
// lives here, keyed by the plan slug used in the API response.

const PLAN_METADATA = {
  'web-only': {
    credits: 1000,
    creditLabel: 'Starter Credits',
    icon: 'globe',
    features: [
      'Plug-and-play no-code web widget',
      'SOC 2, ISO 27001, GDPR compliance',
      'Limited to Knowledge Base Agent',
      'Includes "Powered by CometChat" branding',
    ],
    isHighlighted: false,
    isPayAsYouGo: true,
    payAsYouGoStartPrice: 25,
    order: 1,
  },
  core: {
    credits: 2500,
    creditLabel: 'Monthly Credits',
    icon: 'sparkles',
    features: [
      'UI Kits for all popular web & mobile frameworks',
      'AI moderation & guardrails',
      'Push, email & SMS notifications',
      'Multi-tenancy',
      "No 'Powered by CometChat' branding",
      'HIPAA (BAA) compliance [coming soon]',
      'Advanced search',
      '100+ tools and front-end actions',
    ],
    isHighlighted: true,
    isPayAsYouGo: false,
    order: 2,
  },
  plus: {
    credits: 25000,
    creditLabel: 'Monthly Credits',
    icon: 'sparkles',
    features: [
      'Tasks, workflows & multi-model support',
      'User authentication & memory',
      'Role-based access control (including content)',
      'Human hand-off',
    ],
    isHighlighted: false,
    isPayAsYouGo: false,
    order: 3,
  },
}

// The "Done-for-you" plan is not part of the API catalog — it's a static "contact sales"
// card appended after the API-driven plans.
const DONE_FOR_YOU_PLAN = {
  id: 'done-for-you',
  name: 'Done-for-you',
  description: 'AI agents, built and deployed end-to-end by our team.',
  credits: null,
  creditLabel: '',
  monthlyPrice: null,
  annualPrice: null,
  icon: 'rocket',
  features: [
    'Design, build & deploy by CometChat',
    'Custom integrations & workflows',
    'Dedicated solutions engineer',
    'White-glove onboarding & training',
  ],
  isHighlighted: false,
  isPayAsYouGo: false,
  order: 4,
}

// Read a plan's id/name/description/prices from the API entry. Plans can be flat
// (web-only) or split into monthly/annual sub-objects (core, plus).
const resolveApiPlan = apiPlan => {
  if (!apiPlan) return null
  const monthly = apiPlan.monthly || apiPlan
  const annual = apiPlan.annual || null
  return {
    // Monthly id — used when billing period = monthly and as the subscribe base.
    id: monthly.id || apiPlan.id || null,
    // Annual id — used when billing period = annual.
    annualId: annual?.id ?? null,
    name: apiPlan.name || monthly.name || '',
    description: apiPlan.description || monthly.description || '',
    monthlyPrice: typeof monthly.amount === 'number' ? monthly.amount : null,
    annualPrice:
      annual && typeof annual.monthlyDiscountedAmount === 'number'
        ? annual.monthlyDiscountedAmount
        : null,
  }
}

/**
 * Merge the live `GET /agentic-plans` response with the static presentation metadata into
 * the shape PricingPlansGrid/PricingPlanCard expect. Falls back to an empty list when the
 * API data is missing so the grid renders nothing rather than stale hardcoded prices.
 */
const buildAgenticPlans = apiData => {
  if (!apiData) return []

  const plans = Object.keys(PLAN_METADATA)
    .map(slug => {
      const meta = PLAN_METADATA[slug]
      const apiPlan = resolveApiPlan(apiData[slug])
      if (!apiPlan) return null
      return { ...meta, ...apiPlan }
    })
    .filter(Boolean)

  plans.push(DONE_FOR_YOU_PLAN)
  return plans.sort((a, b) => a.order - b.order)
}

export default buildAgenticPlans
